import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { db } from '../../firebase/firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
// Replaced lucide-react icons with Unicode symbols for React 19 compatibility
import { trackPageView } from '../../firebase/trackingService';
import SEO from '../common/SEO';

// Styled Components
const PageContainer = styled.div`
  background-color: #121212;
  min-height: 100vh;
  color: #FFFFFF;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #1a365d 0%, #0a2540 50%, #0a1929 100%);
  padding: 120px 24px 80px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 100px 24px 60px;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #2196F3 0%, #FFD700 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  max-width: 600px;
  margin: 0 auto 32px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const ContentContainer = styled.main`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  
  @media (max-width: 768px) {
    padding: 60px 24px;
  }
`;

const SearchAndFilterSection = styled.div`
  margin-bottom: 48px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 400px;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 48px;
  background-color: #1E1E1E;
  border: 1px solid #333;
  border-radius: 12px;
  color: #FFFFFF;
  font-size: 16px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 1px #2196F3;
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: "🔍";
    font-size: 16px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const FilterLabel = styled.span`
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
  margin-right: 8px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const FilterButton = styled.button`
  padding: 8px 16px;
  background-color: ${props => props.active ? '#2196F3' : 'transparent'};
  border: 1px solid ${props => props.active ? '#2196F3' : '#333'};
  border-radius: 8px;
  color: ${props => props.active ? '#FFFFFF' : 'rgba(255, 255, 255, 0.7)'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.active ? '#1976D2' : 'rgba(33, 150, 243, 0.1)'};
    border-color: #2196F3;
    color: #FFFFFF;
  }
`;

const BlogGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BlogCard = styled(motion.article)`
  background: linear-gradient(145deg, #1E1E1E 0%, #2A2A2A 100%);
  border-radius: 16px;
  border: 1px solid #333;
  overflow: hidden;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: #2196F3;
  }
`;

const BlogImageContainer = styled.div`
  position: relative;
  aspect-ratio: 16/9;
  overflow: hidden;
`;

const BlogImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${BlogCard}:hover & {
    transform: scale(1.05);
  }
`;

const BlogImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #FFFFFF;
  font-size: 48px;
  font-weight: 700;
`;

const BlogContent = styled.div`
  padding: 24px;
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  background-color: rgba(33, 150, 243, 0.2);
  color: #2196F3;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const BlogTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 12px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogExcerpt = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin-bottom: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  border-top: 1px solid #333;
`;

const ReadMoreLink = styled.div`
  display: flex;
  align-items: center;
  color: #2196F3;
  font-size: 14px;
  font-weight: 600;
  transition: color 0.3s ease;
  
  ${BlogCard}:hover & {
    color: #FFD700;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 0;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 3px solid #333;
  border-top: 3px solid #2196F3;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 24px;
  background: linear-gradient(145deg, #1E1E1E 0%, #2A2A2A 100%);
  border-radius: 16px;
  border: 1px solid #333;
`;

const EmptyStateTitle = styled.h3`
  font-size: 24px;
  color: #FFFFFF;
  margin-bottom: 12px;
`;

const EmptyStateText = styled.p`
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
`;

const NewsletterSection = styled.section`
  margin-top: 80px;
  padding: 48px;
  background: linear-gradient(135deg, #2196F3 0%, #1976D2 100%);
  border-radius: 16px;
  text-align: center;
`;

const NewsletterTitle = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #FFFFFF;
  margin-bottom: 12px;
`;

const NewsletterDescription = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 32px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 12px;
  max-width: 400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 16px;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.5);
    background-color: rgba(255, 255, 255, 0.3);
  }
`;

const NewsletterButton = styled.button`
  padding: 12px 24px;
  background-color: #FFFFFF;
  color: #2196F3;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #FFD700;
    color: #1976D2;
  }
`;

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    trackPageView('Blog', window.location.pathname);
    
    const fetchBlogPosts = async () => {
      try {
        const blogCollection = collection(db, 'blogposts');
        const blogQuery = query(blogCollection, orderBy('createdAt', 'desc'));
        const blogSnapshot = await getDocs(blogQuery);
        const blogList = blogSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setBlogPosts(blogList);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching blog posts:', error);
        setError('Failed to load blog posts');
        setIsLoading(false);
      }
    };

    fetchBlogPosts();
  }, []);

  // Format date to readable format
  const formatDate = (timestamp) => {
    if (!timestamp) return '';

    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Filter posts based on selected category and search term
  const filteredPosts = useMemo(() => {
    let filtered = blogPosts;
    
    // Filter by category
    if (activeFilter !== 'All') {
      filtered = filtered.filter(post => post.category === activeFilter);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(post => 
        post.title?.toLowerCase().includes(searchLower) ||
        post.excerpt?.toLowerCase().includes(searchLower) ||
        post.description?.toLowerCase().includes(searchLower) ||
        post.category?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [blogPosts, activeFilter, searchTerm]);

  // Get unique categories from posts for dynamic filter buttons
  const categories = useMemo(() => {
    const uniqueCategories = new Set();
    blogPosts.forEach(post => {
      if (post.category) {
        uniqueCategories.add(post.category);
      }
    });
    return Array.from(uniqueCategories);
  }, [blogPosts]);

  return (
    <>
      <SEO 
        title="CarConsul Blog - Car Buying Insights & Tips"
        description="Expert guides, tips and resources on car buying, negotiation strategies, vehicle comparisons, and automotive advice"
        pagePath="blog"
      />
      <PageContainer>
        <HeroSection>
          <HeroContent
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <HeroTitle>CarConsul Blog</HeroTitle>
            <HeroSubtitle>
              Expert car buying advice, negotiation strategies, and automotive insights to help you make smarter decisions
            </HeroSubtitle>
          </HeroContent>
        </HeroSection>

        <ContentContainer>
          <SearchAndFilterSection>
            <SearchContainer>
              <SearchIcon />
              <SearchInput
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchContainer>
            
            <FilterContainer>
              <FilterLabel>Filter by:</FilterLabel>
              <FilterButton
                active={activeFilter === 'All'}
                onClick={() => setActiveFilter('All')}
              >
                All
              </FilterButton>
              {categories.map(category => (
                <FilterButton
                  key={category}
                  active={activeFilter === category}
                  onClick={() => setActiveFilter(category)}
                >
                  {category}
                </FilterButton>
              ))}
            </FilterContainer>
          </SearchAndFilterSection>

          {isLoading ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : error ? (
            <EmptyState>
              <EmptyStateTitle>Error Loading Posts</EmptyStateTitle>
              <EmptyStateText>{error}</EmptyStateText>
            </EmptyState>
          ) : filteredPosts.length > 0 ? (
            <BlogGrid
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, staggerChildren: 0.1 }}
            >
              {filteredPosts.map((post, index) => (
                <BlogCard
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -8 }}
                >
                  <Link to={`/blog/${post.slug}`}>
                    <BlogImageContainer>
                      {post.imageUrl ? (
                        <BlogImage
                          src={post.imageUrl}
                          alt={`${post.title} - Car buying advice`}
                          loading="lazy"
                        />
                      ) : (
                        <BlogImagePlaceholder>
                          🚗
                        </BlogImagePlaceholder>
                      )}
                    </BlogImageContainer>
                    <BlogContent>
                      {post.category && (
                        <CategoryBadge>{post.category}</CategoryBadge>
                      )}
                      <BlogTitle>{post.title}</BlogTitle>
                      <BlogExcerpt>
                        {post.excerpt || 
                         post.description || 
                         `${post.htmlContent?.replace(/<[^>]*>/g, '').substring(0, 150)}...`}
                      </BlogExcerpt>
                      <BlogMeta>
                        <ReadMoreLink>
                          Read more →
                        </ReadMoreLink>
                        <MetaInfo>
                          <MetaItem>
                            📅
                            <span>{formatDate(post.createdAt)}</span>
                          </MetaItem>
                          <MetaItem>
                            ⏱️
                            <span>5 min</span>
                          </MetaItem>
                        </MetaInfo>
                      </BlogMeta>
                    </BlogContent>
                  </Link>
                </BlogCard>
              ))}
            </BlogGrid>
          ) : (
            <EmptyState>
              <EmptyStateTitle>
                {searchTerm ? 'No articles found' : 'No articles yet'}
              </EmptyStateTitle>
              <EmptyStateText>
                {searchTerm 
                  ? `No articles match "${searchTerm}". Try different keywords or browse by category.`
                  : activeFilter === 'All'
                  ? 'Check back soon for expert car buying content!'
                  : `No articles in "${activeFilter}" category yet.`
                }
              </EmptyStateText>
            </EmptyState>
          )}

          <NewsletterSection>
            <NewsletterTitle>Stay Informed</NewsletterTitle>
            <NewsletterDescription>
              Get the latest car buying tips, market insights, and negotiation strategies delivered to your inbox
            </NewsletterDescription>
            <NewsletterForm>
              <NewsletterInput
                type="email"
                placeholder="Enter your email"
                required
              />
              <NewsletterButton type="submit">
                Subscribe
              </NewsletterButton>
            </NewsletterForm>
          </NewsletterSection>
        </ContentContainer>
      </PageContainer>
    </>
  );
};

export default BlogPage;